import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, CheckCircle2, AlertCircle, Moon, Sun, ArrowRight, ArrowLeft, Mail, Key, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '@/hooks/useTheme';
import { createPageUrl } from '@/utils';
import { motion, AnimatePresence } from 'framer-motion';

export default function InviteCodeEntry() {
  const { isDark, toggleTheme } = useTheme();
  const [step, setStep] = useState(1);
  const [inviteCode, setInviteCode] = useState('');
  const [email, setEmail] = useState('');
  const [ssnLastFour, setSsnLastFour] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [hireMonthYear, setHireMonthYear] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [requiredChallenges, setRequiredChallenges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const needs = (key) => requiredChallenges.includes(key);

  useEffect(() => {
    // Auto-fill invite code when arriving via magic link
    const params = new URLSearchParams(globalThis.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setInviteCode(tokenParam);
    }
  }, []);

  const validateCode = async () => {
    if (!inviteCode.trim()) {
      setError('Please enter an invitation code');
      return false;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const resp = await axios.get(`${import.meta.env.VITE_API_URL || ''}/api/public/enrollment-token/${inviteCode.trim()}`);
      if (resp.data?.requiresChallenge) {
        setRequiredChallenges(resp.data.requiresChallenge);
      }
      setLoading(false);
      return true;
    } catch (err) {
      setLoading(false);
      setError('Invalid invitation code. Please check and try again.');
      return false;
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const isValid = await validateCode();
      if (isValid) {
        setStep(2);
        setError('');
      }
    } else if (step === 2) {
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
      setStep(3);
      setError('');
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // STEP 1: Validate token + email + identity challenge (public endpoint)
      const validateResponse = await axios.post(
        `${import.meta.env.VITE_API_URL || ''}/api/public/enrollment-token/validate`,
        {
          token: inviteCode.trim(),
          email: email.trim(),
          challenge: {
            ssn_last_four: ssnLastFour.trim(),
            birth_year: birthYear.trim(),
            hire_month_year: hireMonthYear.trim(),
            zip_code: zipCode.trim()
          }
        }
      );

      if (!validateResponse.data.valid) {
        throw new Error('Invalid enrollment information');
      }

      const { firstName, accountExists, requiredChallenges: requiredFromServer } = validateResponse.data;

      if (requiredFromServer && requiredFromServer.length) {
        // If server asks for more challenges we didn't know about (edge case)
        setRequiredChallenges(requiredFromServer);
        setLoading(false);
        return; // Stay on step 3 to fill them
      }

      // STEP 2: Check if user is logged in
      const accessToken = localStorage.getItem('accessToken');
      let user = null;

      try {
        if (accessToken) {
          user = await api.auth.me();
        }
      } catch (err) {
        console.warn('Auth check failed during invite handling:', err);
        user = null;
      }

      if (!user) {
        // Not logged in - redirect to login/register with state
        toast.info(accountExists ? 'Please log in to continue.' : 'Activate your account to continue.');
        sessionStorage.setItem('enrollmentToken', inviteCode.trim());
        sessionStorage.setItem('enrollmentEmail', email.trim());
        const redirect = encodeURIComponent('/InviteCodeEntry');
        if (accountExists) {
          globalThis.location.href = `/Login?redirect=${redirect}`;
        } else {
          globalThis.location.href = `/Login?mode=register&activation=1&redirect=${redirect}`;
        }
        return;
      }

      // STEP 3: Verify logged-in user's email matches
      if (user.email?.toLowerCase() !== email.toLowerCase()) {
        setError(
          `You're signed in as ${user.email}, but this invitation is for ${email}. ` +
          `Please sign out and try again with the correct email address.`
        );
        setLoading(false);
        return;
      }

      // STEP 4: Claim the token (authenticated)
      const claimResponse = await api.apiClient.post(
        '/public/enrollment-token/claim',
        { token: inviteCode.trim() }
      );

      if (claimResponse.data.success) {
        // Update local user context
        const updatedUser = {
          ...user,
          employerId: claimResponse.data.employerId,
          employeeId: claimResponse.data.employeeId
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));

        // Navigate based on onboarding status
        toast.success(`Welcome, ${firstName}! Let's get you set up.`);
        
        const onboardingNeeded = claimResponse.data.needsOnboarding || (claimResponse.data.lastOnboardingStep && claimResponse.data.lastOnboardingStep !== 'done');
        if (onboardingNeeded) {
          globalThis.location.href = createPageUrl('EmployeeOnboarding');
        } else {
          // Resume enrollment based on last step
          const step = claimResponse.data.lastEnrollmentStep;
          if (!step || step === 'intro') {
            globalThis.location.href = createPageUrl('EnrollmentIntro');
          } else if (step === 'select') {
            globalThis.location.href = createPageUrl('BenefitsCatalog');
          } else if (step === 'review') {
            globalThis.location.href = createPageUrl('EnrollmentReview');
          } else if (step === 'sign') {
            globalThis.location.href = createPageUrl('EnrollmentSign');
          } else {
            globalThis.location.href = createPageUrl('EmployeeDashboard');
          }
        }
      }
    } catch (err) {
      console.error('Invite acceptance error:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Invalid enrollment information. Please verify your details and try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="invite-code">Invitation Code</Label>
        <div className="relative">
          <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="invite-code"
            value={inviteCode}
            onChange={(e) => setInviteCode(e.target.value)}
            placeholder="Enter your code"
            className="pl-9 text-center tracking-wider font-mono text-lg uppercase"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleNext()}
          />
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Check your email for the 6-character code
        </p>
      </div>
    </motion.div>
  );

  const renderStep2 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@company.com"
            className="pl-9"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleNext()}
          />
        </div>
        <p className="text-xs text-muted-foreground text-center">
          Use the email address where you received the invite
        </p>
      </div>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div className="text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">Identity Verification</p>
            <p>To protect your benefits information, please verify your identity.</p>
          </div>
        </div>
      </div>

      {requiredChallenges.length === 0 && (
        <div className="text-center py-4">
          <UserCheck className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
          <p className="text-lg font-medium">Ready to verify!</p>
          <p className="text-sm text-muted-foreground">Click below to continue.</p>
        </div>
      )}

      {needs('ssn_last_four') && (
        <div>
          <Label htmlFor="ssn">Last 4 Digits of SSN</Label>
          <Input
            id="ssn"
            type="text"
            value={ssnLastFour}
            onChange={(e) => setSsnLastFour(e.target.value.replaceAll(/\D/g, '').slice(0, 4))}
            placeholder="1234"
            required
            maxLength={4}
            pattern="[0-9]{4}"
            className="text-center tracking-widest font-mono"
          />
        </div>
      )}

      {needs('birth_year') && (
        <div>
          <Label htmlFor="birth-year">Birth Year (YYYY)</Label>
          <Input
            id="birth-year"
            type="text"
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value.replaceAll(/\D/g, '').slice(0, 4))}
            placeholder="1985"
            required
            maxLength={4}
            pattern="[0-9]{4}"
            className="text-center font-mono"
          />
        </div>
      )}

      {needs('hire_month_year') && (
        <div>
          <Label htmlFor="hire-date">Hire Month/Year (MM/YYYY)</Label>
          <Input
            id="hire-date"
            type="text"
            value={hireMonthYear}
            onChange={(e) => setHireMonthYear(e.target.value.replaceAll(/[^0-9/]/g, '').slice(0, 7))}
            placeholder="04/2022"
            required
            pattern="^(0[1-9]|1[0-2])\/\d{4}$"
            className="text-center font-mono"
          />
        </div>
      )}

      {needs('zip_code') && (
        <div>
          <Label htmlFor="zip">ZIP Code</Label>
          <Input
            id="zip"
            type="text"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value.replaceAll(/\D/g, '').slice(0, 10))}
            placeholder="90210"
            required
            className="text-center font-mono"
          />
        </div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300 flex flex-col">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => { globalThis.location.href = '/'; }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md shadow-2xl border-t-4 border-t-emerald-500">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold flex items-center justify-center gap-2">
              {step === 1 && 'Welcome!'}
              {step === 2 && 'Verify Email'}
              {step === 3 && 'Confirm Identity'}
            </CardTitle>
            <CardDescription>
              {step === 1 && 'Enter your invitation code to get started'}
              {step === 2 && 'Please confirm your email address'}
              {step === 3 && 'One last step to secure your account'}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            {/* Progress Steps */}
            <div className="flex justify-center gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    s === step ? 'w-8 bg-emerald-500' : s < step ? 'w-2 bg-emerald-500' : 'w-2 bg-slate-200 dark:bg-slate-700'
                  }`}
                />
              ))}
            </div>

            <AnimatePresence mode="wait">
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
            </AnimatePresence>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4"
              >
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </motion.div>
            )}
          </CardContent>

          <CardFooter className="flex justify-between pt-2">
            {step > 1 ? (
              <Button variant="ghost" onClick={handleBack} disabled={loading}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <Button 
                onClick={handleNext} 
                disabled={loading}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Complete Setup
                  </>
                )}
              </Button>
            )}
          </CardFooter>
        </Card>
      </div>
      
      <div className="text-center py-6 text-sm text-muted-foreground">
        Need help? Contact your HR department
      </div>
    </div>
  );
}